// Bangladesh board-style academic transcript (marksheet), printable / save-as-PDF from the browser.
// Bilingual: renders in Bangla when the app language is Bangla, otherwise English.

const BN_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
const esc = (v) => String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const L = {
  en: {
    title: 'ACADEMIC TRANSCRIPT', eiin: 'EIIN', board: 'Board', name: "Student's Name", father: "Father's Name", mother: "Mother's Name",
    cls: 'Class', section: 'Section', roll: 'Roll No.', adm: 'Admission No.', group: 'Group', shift: 'Shift', version: 'Version',
    bRoll: 'Board Roll', bReg: 'Board Registration', dob: 'Date of Birth', session: 'Session', sl: 'SL', subject: 'Subject',
    full: 'Full Marks', got: 'Marks Obtained', grade: 'Letter Grade', gp: 'Grade Point', fourth: '(4th subject)',
    total: 'Total Marks', pct: 'Percentage', gpa: 'GPA (out of 5.00)', finalGrade: 'Final Grade', result: 'Result',
    passed: 'PASSED', failed: 'FAILED', rank: 'Position in Class', scale: 'Grading Scale', range: 'Marks (%)',
    teacher: 'Class Teacher', head: 'Head Master / Principal', guardian: 'Guardian', issued: 'Date of Issue',
    note: 'Failing in any compulsory subject results in GPA 0.00. In the 4th subject only the grade point above 2.00 is added to the total.',
  },
  bn: {
    title: 'একাডেমিক ট্রান্সক্রিপ্ট', eiin: 'ইআইআইএন', board: 'শিক্ষা বোর্ড', name: 'শিক্ষার্থীর নাম', father: 'পিতার নাম', mother: 'মাতার নাম',
    cls: 'শ্রেণি', section: 'শাখা', roll: 'রোল নং', adm: 'ভর্তি নং', group: 'বিভাগ', shift: 'শিফট', version: 'ভার্সন',
    bRoll: 'বোর্ড রোল', bReg: 'বোর্ড রেজিস্ট্রেশন', dob: 'জন্ম তারিখ', session: 'শিক্ষাবর্ষ', sl: 'ক্রম', subject: 'বিষয়',
    full: 'পূর্ণমান', got: 'প্রাপ্ত নম্বর', grade: 'লেটার গ্রেড', gp: 'গ্রেড পয়েন্ট', fourth: '(৪র্থ বিষয়)',
    total: 'মোট নম্বর', pct: 'শতকরা', gpa: 'জিপিএ (৫.০০ এর মধ্যে)', finalGrade: 'চূড়ান্ত গ্রেড', result: 'ফলাফল',
    passed: 'উত্তীর্ণ', failed: 'অনুত্তীর্ণ', rank: 'শ্রেণিতে মেধাক্রম', scale: 'গ্রেডিং পদ্ধতি', range: 'নম্বর (%)',
    teacher: 'শ্রেণি শিক্ষক', head: 'প্রধান শিক্ষক / অধ্যক্ষ', guardian: 'অভিভাবক', issued: 'প্রদানের তারিখ',
    note: 'কোনো আবশ্যিক বিষয়ে অকৃতকার্য হলে জিপিএ ০.০০ হবে। ৪র্থ বিষয়ে ২.০০ এর উপরের গ্রেড পয়েন্ট মোট পয়েন্টের সাথে যোগ হয়।',
  },
};

const SCALE = [['80 - 100', 'A+', '5.00'], ['70 - 79', 'A', '4.00'], ['60 - 69', 'A-', '3.50'], ['50 - 59', 'B', '3.00'], ['40 - 49', 'C', '2.00'], ['33 - 39', 'D', '1.00'], ['0 - 32', 'F', '0.00']];

export function printBdTranscript({ result, examTerm, institution = {}, student = {}, isBangla = false }) {
  const t = isBangla ? L.bn : L.en;
  const d = (v) => (isBangla ? String(v ?? '').replace(/[0-9]/g, (x) => BN_DIGITS[+x]) : String(v ?? ''));
  const fixed = (n, k = 2) => d(Number(n || 0).toFixed(k));
  const subjects = result.subjects || [];
  const passed = !!result.is_pass;
  const school = institution.school_name || institution.name || 'School';
  const rows = subjects.map((s, i) => `
    <tr>
      <td class="c">${d(i + 1)}</td>
      <td>${esc(s.subject_name)} ${s.is_optional ? `<span class="muted">${t.fourth}</span>` : ''}</td>
      <td class="c">${d(s.max_marks)}</td>
      <td class="c">${d(s.obtained_marks)}</td>
      <td class="c b">${esc(s.grade || '-')}</td>
      <td class="c">${typeof s.grade_point === 'number' ? fixed(s.grade_point) : '-'}</td>
    </tr>`).join('');
  const scale = SCALE.map(([r, g, p]) => `<tr><td class="c">${d(r)}</td><td class="c b">${g}</td><td class="c">${d(p)}</td></tr>`).join('');
  const cell = (label, val) => `<div class="cell"><span class="lbl">${label}</span><span class="val">${esc(val || '-')}</span></div>`;

  const html = `<!doctype html><html lang="${isBangla ? 'bn' : 'en'}"><head><meta charset="utf-8">
<title>${esc(t.title)} - ${esc(result.student_name)}</title>
<style>
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Noto Sans Bengali','Nirmala UI','Kalpurush','Shonar Bangla','Segoe UI',Arial,sans-serif; color:#111; margin:0; }
  .sheet { border: 3px double #14532d; padding: 16px 20px; min-height: 270mm; display:flex; flex-direction:column; }
  .head { text-align:center; border-bottom: 2px solid #14532d; padding-bottom: 10px; }
  .head img { height: 64px; vertical-align: middle; margin-right: 12px; }
  .school { font-size: 24px; font-weight: 800; color:#14532d; }
  .meta { font-size: 12px; color:#374151; margin-top: 2px; }
  .title { display:inline-block; margin-top: 10px; padding: 4px 22px; border: 1.5px solid #14532d; border-radius: 4px; font-weight: 800; letter-spacing: 1px; font-size: 16px; color:#14532d; }
  .exam { font-size: 13px; margin-top: 6px; font-weight: 600; }
  .grid { display:grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; margin: 14px 0; font-size: 13px; }
  .cell { display:flex; gap:8px; border-bottom: 1px dotted #9ca3af; padding: 3px 0; }
  .lbl { min-width: 120px; color:#4b5563; }
  .val { font-weight: 700; }
  table { width:100%; border-collapse: collapse; font-size: 13px; }
  th, td { border: 1px solid #374151; padding: 6px 8px; }
  th { background:#ecfdf5; }
  .c { text-align:center; } .b { font-weight:700; } .muted { color:#6b7280; font-size: 11px; }
  .summary { display:grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 14px; }
  .box { border: 1.5px solid #14532d; border-radius: 6px; text-align:center; padding: 8px 4px; }
  .box .k { font-size: 11px; color:#4b5563; } .box .v { font-size: 20px; font-weight: 800; }
  .pass { color:#15803d; } .fail { color:#b91c1c; }
  .lower { display:grid; grid-template-columns: 1.2fr 1fr; gap: 18px; margin-top: 16px; align-items:start; }
  .note { font-size: 11px; color:#4b5563; margin-top: 8px; }
  .sign { margin-top: auto; padding-top: 46px; display:flex; justify-content: space-between; font-size: 12px; }
  .sign div { width: 30%; text-align:center; border-top: 1px solid #111; padding-top: 4px; }
  .issued { margin-top: 18px; font-size: 12px; }
</style></head><body>
<div class="sheet">
  <div class="head">
    ${institution.logo_url ? `<img src="${esc(institution.logo_url)}" alt="">` : ''}<span class="school">${esc(school)}</span>
    <div class="meta">${esc(institution.address || '')}</div>
    <div class="meta">${[institution.eiin ? `${t.eiin}: ${d(institution.eiin)}` : '', institution.education_board ? `${t.board}: ${esc(institution.education_board)}` : ''].filter(Boolean).join('  |  ')}</div>
    <div class="title">${t.title}</div>
    <div class="exam">${esc(examTerm?.name || '')} ${examTerm?.academic_year ? '- ' + d(examTerm.academic_year) : ''}</div>
  </div>
  <div class="grid">
    ${cell(t.name, result.student_name)}${cell(t.roll, d(student.roll_no || ''))}
    ${cell(t.father, student.father_name)}${cell(t.adm, d(result.admission_no))}
    ${cell(t.mother, student.mother_name)}${cell(t.dob, d(student.date_of_birth))}
    ${cell(t.cls, isBangla ? d(String(result.class_name || '').replace(/^Class\s*/i, 'শ্রেণি ')) : result.class_name)}${cell(t.section, result.section_name)}
    ${cell(t.group, student.student_group)}${cell(t.shift, student.shift)}
    ${cell(t.version, student.version)}${cell(t.session, d(examTerm?.academic_year))}
    ${cell(t.bRoll, d(student.board_roll_no))}${cell(t.bReg, d(student.board_registration_no))}
  </div>
  <table>
    <thead><tr><th style="width:44px">${t.sl}</th><th>${t.subject}</th><th>${t.full}</th><th>${t.got}</th><th>${t.grade}</th><th>${t.gp}</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="summary">
    <div class="box"><div class="k">${t.total}</div><div class="v">${d(result.total_marks)} / ${d(result.total_max_marks)}</div></div>
    <div class="box"><div class="k">${t.pct}</div><div class="v">${fixed(result.percentage)}%</div></div>
    <div class="box"><div class="k">${t.gpa}</div><div class="v">${fixed(result.gpa)}</div></div>
    <div class="box"><div class="k">${t.result}</div><div class="v ${passed ? 'pass' : 'fail'}">${passed ? t.passed : t.failed}</div></div>
  </div>
  <div class="lower">
    <div>
      <div class="cell"><span class="lbl">${t.finalGrade}</span><span class="val">${esc(result.grade || '-')}</span></div>
      ${result.rank ? `<div class="cell"><span class="lbl">${t.rank}</span><span class="val">${d(result.rank)}</span></div>` : ''}
      <div class="note">${t.note}</div>
    </div>
    <div>
      <table><thead><tr><th colspan="3">${t.scale}</th></tr><tr><th>${t.range}</th><th>${t.grade}</th><th>${t.gp}</th></tr></thead><tbody>${scale}</tbody></table>
    </div>
  </div>
  <div class="issued">${t.issued}: ${d(new Date().toLocaleDateString('en-GB'))}</div>
  <div class="sign"><div>${t.teacher}</div><div>${t.head}</div><div>${t.guardian}</div></div>
</div>
<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 400); };</script>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  return true;
}

export default printBdTranscript;
